/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useMemo, useState } from "react";

import cn from "classnames";
import moment from "moment";

import SessionPlaceholder from "assets/tripdetails/SessionPlaceholder.png";
import { UnGuard } from "constants/interfaces";
import { GetTripsResponse } from "lib/tripsApiTypes";
import { useUI } from "state/UIContext";
import { useOrgData } from "state/OrgDataContext";
import getFormattedDate from "utils/getFormattedDate";

import {
  Button,
  HR,
  Heading,
  IMG,
  Stack,
  Text,
  QRModal,
  FontAwesomeIcon,
} from "components";
import { useTranslation } from "react-i18next";
import { pushToDataLayer } from "utils/gtm";

type Sessions = UnGuard<GetTripsResponse["approved_trips"]>[number]["sessions"];
type SessionItem = UnGuard<UnGuard<Sessions>[string]>[number];

const TIME_FORMATS = [
  "HH:mm:ss",
  "HH:mm",
  "hh:mm A",
  "h:mm A",
  moment.ISO_8601,
];

const toTimestamp = (value?: string) => {
  if (!value) return Number.POSITIVE_INFINITY;
  const parsed = moment(value, TIME_FORMATS);
  return parsed.isValid() ? parsed.valueOf() : Number.POSITIVE_INFINITY;
};

/** Sessions arrive grouped by day in arbitrary order — sort days, then each day's sessions by start time */
const sortSessions = (sessions: Sessions) =>
  Object.entries(sessions ?? {})
    .sort(([a], [b]) => toTimestamp(a) - toTimestamp(b))
    .map(
      ([dayKey, daySessions]) =>
        [
          dayKey,
          [...(daySessions ?? [])].sort(
            (a: SessionItem, b: SessionItem) =>
              toTimestamp(a?.from) - toTimestamp(b?.from) ||
              toTimestamp(a?.till) - toTimestamp(b?.till),
          ),
        ] as const,
    );

const MatchSection = ({
  sessions,
  blinkId,
}: {
  sessions: Sessions;
  blinkId: string;
}) => {
  const sortedSessions = useMemo(() => sortSessions(sessions), [sessions]);

  const [isExpanded, setExpanded] = useState(() => {
    return Object.keys(sessions ?? {}).reduce<Record<string, boolean>>(
      (acc, day) => {
        acc[day] = true;
        return acc;
      },
      {},
    );
  });

  return (
    <Stack className="gap-y-5 md:gap-y-10">
      {sortedSessions.map(([dayKey, daySessions]) => (
        <Stack key={dayKey} className="">
          <Stack className="items-start -bg--theme-colors-surface-neutral-level03 -rounded-heading-container mb-5 md:mb-0">
            <Button
              variant={"ghost-unstyled"}
              className="p-0"
              onClick={() => {
                setExpanded((prev) => ({
                  ...prev,
                  [dayKey]: !prev[dayKey],
                }));
              }}
            >
              <div className="flex gap-x-1.5 -text--theme-colors-action-ghost-default-fg items-center">
                <div className="p-2">
                  <FontAwesomeIcon
                    className={cn(
                      !isExpanded[dayKey] ? "" : "rotate-180",
                      "-text--theme-colors-action-ghost-default-fg grow p-1",
                    )}
                    name="fa-chevron-down"
                    size="xs"
                  />
                </div>

                <Text className="-text--theme-colors-text-subtitle-brand font-bold leading-6 text-base">
                  {getFormattedDate(dayKey)}
                </Text>
              </div>
            </Button>
          </Stack>
          {isExpanded[dayKey] &&
            daySessions?.map((session, i) => (
              <div key={i}>
                <Session session={session} number={i + 1} blinkId={blinkId} />
                {i !== daySessions.length - 1 && <HR className="mt-5 mb-0" />}
              </div>
            ))}
        </Stack>
      ))}
    </Stack>
  );
};

const Session = ({
  session,
  number,
  blinkId,
}: {
  session: UnGuard<
    UnGuard<
      UnGuard<GetTripsResponse["approved_trips"]>[number]["sessions"]
    >[string]
  >[number];
  number: number;
  blinkId: string;
}) => {
  const { t } = useTranslation();
  const { openModal } = useUI();
  const { dataContent: EVENT_CONTENT } = useOrgData();

  const openVMSModal = () => {
    const userIdQRCodeUrl = `${EVENT_CONTENT.userIdQRCode},${blinkId}`;
    const isVMS = !!session?.vms_available;

    if (!isVMS) {
      pushToDataLayer({
        event: "view_user_qr_code",
        screen_name: "dashboard",
        journey_step: "dashboard",
      });
    }

    openModal({
      content: (
        <QRModal
          qrUrl={isVMS ? session?.qr_code || "" : userIdQRCodeUrl}
          title={
            isVMS
              ? t("matchSection.vmsSessionQRCodeModalTitle")
              : t("guestDetails.formFields.userIdQRCodeModalTitle")
          }
        />
      ),
    });
  };

  return (
    <Stack className="gap-y-2.5 mt-5 overflow-hidden">
      <Heading
        as="h4"
        isBold={true}
        className="-text--theme-colors-text-subtitle-brand leading-6"
      >
        {t("matchSection.sessionLabel")} {number}
      </Heading>

      <div className="flex flex-col md:items-center items-start lg:flex-row space-y-8 lg:space-y-s0 lg:gap-x-8 min-w-0">
        <IMG
          className="hidden md:block h-[170px] w-[300px] object-cover rounded-sg shrink-0"
          src={session?.cover_image || SessionPlaceholder}
          alt="hotel-img"
        />

        <div className="flex flex-col flex-1 -text--theme-colors-text-body w-full min-w-0 overflow-hidden">
          <Stack className="gap-y-5">
            <div className="flex items-center w-full justify-between">
              <SectionItem
                title={t("matchSection.titleLabel")}
                subTitle={session?.title}
                className="font-bold"
              />

              <Button
                variant="secondary"
                onClick={() => {
                  openVMSModal();
                }}
                className="!min-h-min !p-[5px] !-rounded-icon-button"
              >
                <div className="p-1.5">
                  <FontAwesomeIcon name="fa-qrcode" size="lg" />
                </div>
              </Button>
            </div>

            <SectionItem
              title={t("matchSection.venueLabel")}
              subTitle={session.venue_name}
            />

            <Stack className="md:flex-row gap-x-10 gap-y-5">
              <SectionItem
                title={t("matchSection.timeLabel")}
                subTitle={`${session.from} - ${session.till}`}
              />

              <SectionItem
                title={t("matchSection.dateLabel")}
                subTitle={getFormattedDate(session.date)}
              />
            </Stack>
          </Stack>
        </div>
      </div>
    </Stack>
  );
};

const SectionItem = ({
  title,
  subTitle,
  className,
}: {
  title?: string | null;
  subTitle?: string | null;
  className?: string;
}) => {
  return (
    <div className="flex flex-col gap-y-1 min-w-0">
      <Heading
        as="h4"
        isBold={false}
        className="-text--theme-colors-text-subtitle-brand text-sm"
      >
        {title}
      </Heading>

      <Text className={cn("line-clamp-4 break-all pe-1", className)}>
        {subTitle}
      </Text>
    </div>
  );
};

export default MatchSection;
